import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenCheckIcon, BoxIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, LockKeyholeIcon, PlusIcon, RotateCcwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { setUserCustomFlashcardSets, subscribeToUserCustomFlashcardSets, type CustomFlashcardSet } from '../../../utils/firebase';
import { getCheckoutUrl, getStudyProgram } from '../../../utils/studyAccess';
import { useStudyMembership } from '../../../utils/useStudyMembership';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
const fieldClass = 'mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15';
type StudyView = 'dashboard' | 'quiz-sections' | 'quiz' | 'flashcard-library' | 'create-flashcard-set' | 'flashcards';
type QuizSection = {
  id: string;
  title: string;
  description: string;
  pastScore: string;
  questionCount: number;
};
type QuestionFormat = 'multiple-choice' | 'open-ended' | 'matching' | 'true-false';
type QuizSettings = {
  questionCount: number;
  timeLimitMinutes: number | null;
  revealIncorrectAnswers: boolean;
  formats: QuestionFormat[];
};
type Flashcard = {
  topic: string;
  front: string;
  back: string;
};
type FlashcardSet = {
  id: string;
  title: string;
  description: string;
  cardCount: number;
  cards: Flashcard[];
  isCustom?: boolean;
};
const questions = [{
  prompt: 'Which action is most appropriate before initiating parenteral nutrition in a patient at high risk for refeeding syndrome?',
  answers: ['Advance to goal calories on day one', 'Assess and correct key electrolyte abnormalities', 'Avoid protein during the first 48 hours', 'Use peripheral access for all formulations'],
  correct: 1,
  rationale: 'High-risk patients require careful baseline assessment and correction of electrolyte abnormalities, with calories advanced cautiously.'
}, {
  prompt: 'For a stable adult receiving enteral nutrition, which measure is most useful for evaluating tolerance over time?',
  answers: ['A single gastric residual volume', 'Trend in clinical status, intake, stool pattern, and abdominal findings', 'The patient’s height alone', 'Daily interruption of the feeding'],
  correct: 1,
  rationale: 'Tolerance is a clinical pattern, not a single number. Consider the full picture and institutional protocols.'
}];
const flashcardSets: FlashcardSet[] = [{
  id: 'nutrition-assessment',
  title: 'Nutrition Assessment',
  description: 'Screening, clinical findings, and practical assessment priorities.',
  cardCount: 24,
  cards: [{
    topic: 'Nutrition assessment',
    front: 'What should be evaluated when assessing nutrition support tolerance?',
    back: 'Clinical status, delivery volume, GI findings, hydration, labs, and individual response.'
  }, {
    topic: 'Nutrition assessment',
    front: 'What is the purpose of nutrition screening?',
    back: 'To quickly identify people at risk who need a more comprehensive nutrition assessment.'
  }, {
    topic: 'Nutrition assessment',
    front: 'Why is a weight trend more useful than a single weight?',
    back: 'A trend provides context about change over time and helps identify clinically meaningful loss or gain.'
  }]
}, {
  id: 'medical-nutrition-therapy',
  title: 'Medical Nutrition Therapy',
  description: 'High-yield interventions for common clinical nutrition scenarios.',
  cardCount: 32,
  cards: [{
    topic: 'Medical nutrition therapy',
    front: 'What is a core goal of individualized medical nutrition therapy?',
    back: 'To translate clinical needs into realistic nutrition interventions that support health outcomes.'
  }, {
    topic: 'Medical nutrition therapy',
    front: 'When should education be adapted for readiness to learn?',
    back: 'Whenever barriers, literacy, culture, motivation, or the care setting affect a person’s ability to act on guidance.'
  }, {
    topic: 'Medical nutrition therapy',
    front: 'Why should follow-up be part of an intervention plan?',
    back: 'It lets the dietitian assess response, resolve barriers, and adjust the plan over time.'
  }]
}, {
  id: 'nutrition-support',
  title: 'Nutrition Support',
  description: 'Enteral and parenteral nutrition principles for clinical practice.',
  cardCount: 28,
  cards: [{
    topic: 'Parenteral nutrition',
    front: 'What is one key goal when advancing nutrition in someone at refeeding risk?',
    back: 'Advance cautiously while closely monitoring and repleting electrolytes as clinically indicated.'
  }, {
    topic: 'Enteral nutrition',
    front: 'What is a core advantage of enteral nutrition when the GI tract is functional?',
    back: 'It supports use of the gut and is generally preferred when clinically appropriate.'
  }, {
    topic: 'Nutrition support',
    front: 'What is a key consideration when monitoring nutrition support?',
    back: 'Monitor delivery, tolerance, fluid balance, labs, and the person’s changing clinical condition.'
  }]
}];
const topics = [['Nutrition assessment', 78], ['Enteral nutrition', 64], ['Parenteral nutrition', 49], ['Clinical management', 56]];
const quizSections: QuizSection[] = [{
  id: 'nutrition-assessment',
  title: 'Nutrition Assessment',
  description: 'Screening, anthropometrics, and clinical assessment findings.',
  pastScore: '78% · 8 questions',
  questionCount: 12
}, {
  id: 'medical-nutrition-therapy',
  title: 'Medical Nutrition Therapy',
  description: 'Nutrition diagnoses, interventions, and follow-up planning.',
  pastScore: '84% · 10 questions',
  questionCount: 14
}, {
  id: 'enteral-nutrition',
  title: 'Enteral Nutrition',
  description: 'Formula selection, access routes, delivery, and monitoring.',
  pastScore: '64% · 11 questions',
  questionCount: 16
}, {
  id: 'parenteral-nutrition',
  title: 'Parenteral Nutrition',
  description: 'Indications, formulation, complications, and refeeding risk.',
  pastScore: '49% · 9 questions',
  questionCount: 15
}, {
  id: 'clinical-management',
  title: 'Clinical Management',
  description: 'Clinical decision-making across complex nutrition scenarios.',
  pastScore: '72% · 7 questions',
  questionCount: 13
}];
export function Study() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const selectedProduct = getStudyProgram(searchParams.get('product'));
  const membership = useStudyMembership(selectedProduct?.id);
  const selectedProgram = selectedProduct?.name ?? 'CNSC Exam Prep';
  const [view, setView] = useState<StudyView>('dashboard');
  const [selectedFlashcardSet, setSelectedFlashcardSet] = useState<FlashcardSet | null>(null);
  const [selectedQuizSectionIds, setSelectedQuizSectionIds] = useState<string[]>([]);
  const [quizSettings, setQuizSettings] = useState<QuizSettings>({
    questionCount: 10,
    timeLimitMinutes: null,
    revealIncorrectAnswers: true,
    formats: ['multiple-choice']
  });
  const [customSets, setCustomSets] = useState<CustomFlashcardSet[]>([]);
  const [customSetsLoading, setCustomSetsLoading] = useState(Boolean(user));
  const [customSetsError, setCustomSetsError] = useState<string | null>(null);
  useEffect(() => {
    if (!user) {
      setCustomSets([]);
      setCustomSetsLoading(false);
      setCustomSetsError(null);
      return;
    }
    setCustomSetsLoading(true);
    setCustomSetsError(null);
    return subscribeToUserCustomFlashcardSets(user.uid, sets => {
      setCustomSets(sets);
      setCustomSetsLoading(false);
    }, () => {
      setCustomSetsLoading(false);
      setCustomSetsError('Your saved sets could not be loaded. Please refresh and try again.');
    });
  }, [user?.uid]);
  const openFlashcardLibrary = () => {
    setSelectedFlashcardSet(null);
    setView('flashcard-library');
  };
  const selectSet = (set: FlashcardSet) => {
    setSelectedFlashcardSet(set);
    setView('flashcards');
  };
  const saveCustomSet = async (set: CustomFlashcardSet) => {
    if (!user) throw new Error('Please sign in to save your flashcard set.');
    await setUserCustomFlashcardSets(user.uid, [set, ...customSets]);
    selectSet(toStudySet(set));
  };
  const openQuizSections = () => {
    setSelectedQuizSectionIds([]);
    setView('quiz-sections');
  };
  const toggleQuizSection = (sectionId: string) => {
    setSelectedQuizSectionIds(current => current.includes(sectionId) ? current.filter(id => id !== sectionId) : [...current, sectionId]);
  };
  const selectedQuizSections = quizSections.filter(section => selectedQuizSectionIds.includes(section.id));
  if (!isPreview && !selectedProduct) {
    return <InvalidStudyProgramState data-id="element-2916" data-mp="1xuwzjuqm3goy" />;
  }
  if (!isPreview && (authLoading || membership.status === 'loading')) {
    return <LoadingState data-id="element-2917" data-mp="2po5tl6alvro8" />;
  }
  if (!isPreview && !user) {
    navigate('/login', {
      state: {
        from: `${location.pathname}${location.search}`
      },
      replace: true
    });
    return <LoadingState data-id="element-2918" data-mp="20o1fcubd80cl" />;
  }
  if (!isPreview && !membership.hasAccess && selectedProduct) {
    return <LockedStudyState programId={selectedProduct.id} programName={selectedProduct.name} data-id="element-2919" data-mp="25mulu2s8ir39" />;
  }
  return <div className="min-h-screen bg-white px-4 py-7 text-[#1E293B] md:px-8 md:py-10" style={{
    fontFamily: FONT
  }} data-id="element-2920" data-mp="1qotvrpe1f9h1">
      <div className="mx-auto max-w-7xl" data-id="element-2921" data-mp="1vqrkv094cyhw">
        <header className="mb-7 flex flex-col justify-between gap-5 border-b border-[#EDF1F7] pb-6 md:flex-row md:items-end" data-id="element-2922" data-mp="1hwig8ykgp6fu">
          <div data-id="element-2923" data-mp="1mptguhyokqzb">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2924" data-mp="1mqcxlvclh9br">
              RD Ready member space
            </p>
            <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-2925" data-mp="1cxnnp4wp03ij">
              {selectedProgram}
            </h1>
            <p className="mt-2 text-sm text-[#5A6B7B]" data-id="element-2926" data-mp="yz7f18f9s2xr">Study with intention.</p>
          </div>
          <nav className="flex flex-wrap gap-2" aria-label="Study navigation" data-id="element-2927" data-mp="12zrfakv53mo4">
            {([['dashboard', 'Overview'], ['quiz-sections', 'Quiz mode'], ['flashcard-library', 'Flashcards']] as [StudyView, string][]).map(([target, label]) => {
            const active = view === target || target === 'quiz-sections' && view === 'quiz' || target === 'flashcard-library' && (view === 'flashcards' || view === 'create-flashcard-set');
            return <button key={target} type="button" onClick={() => target === 'flashcard-library' ? openFlashcardLibrary() : target === 'quiz-sections' ? openQuizSections() : setView(target)} className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors ${active ? 'text-white' : 'border border-[#E2E8F0] bg-white text-[#475569] hover:border-[#2563EB] hover:text-[#2563EB]'}`} style={active ? {
              backgroundColor: BLUE
            } : undefined} data-id="element-2928" data-mp="y9xdx0t917vh">
                  {label}
                </button>;
          })}
          </nav>
        </header>

        {view === 'dashboard' && <Dashboard onStartQuiz={openQuizSections} onStartCards={openFlashcardLibrary} data-id="element-2929" data-mp="12d37nt9at0jh" />}
        {view === 'quiz-sections' && <QuizSectionChooser sections={quizSections} selectedIds={selectedQuizSectionIds} onBack={() => setView('dashboard')} onToggle={toggleQuizSection} onStart={settings => {
        setQuizSettings(settings);
        setView('quiz');
      }} data-id="element-2930" data-mp="1bfcfgbd6mft6" />}
        {view === 'quiz' && <Quiz selectedSections={selectedQuizSections} settings={quizSettings} onBack={() => setView('quiz-sections')} onFinish={() => setView('dashboard')} data-id="element-2931" data-mp="30whnnnn8mqnf" />}
        {view === 'flashcard-library' && <FlashcardLibrary builtInSets={flashcardSets} customSets={customSets.map(toStudySet)} customSetsLoading={customSetsLoading} customSetsError={customSetsError} onCreate={() => setView('create-flashcard-set')} onSelect={selectSet} data-id="element-2932" data-mp="2qvyzigon4nzp" />}
        {view === 'create-flashcard-set' && <CreateFlashcardSet onCancel={openFlashcardLibrary} onSave={saveCustomSet} data-id="element-2933" data-mp="3alhntadh7s37" />}
        {view === 'flashcards' && selectedFlashcardSet && <Flashcards set={selectedFlashcardSet} onBack={openFlashcardLibrary} onFinish={() => setView('dashboard')} data-id="element-2934" data-mp="1z5ax71b8dckg" />}
      </div>
    </div>;
}
function toStudySet(set: CustomFlashcardSet): FlashcardSet {
  return {
    id: set.id,
    title: set.title,
    description: set.description,
    cardCount: set.cards.length,
    cards: set.cards.map(card => ({
      topic: card.topic || 'Your study notes',
      front: card.front,
      back: card.back
    })),
    isCustom: true
  };
}
function LoadingState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white" data-id="element-2935" data-mp="empb0hj20xc8">
      <span className="h-7 w-7 animate-spin rounded-full border-2 border-t-transparent" style={{
      borderColor: BLUE,
      borderTopColor: 'transparent'
    }} data-id="element-2936" data-mp="oikxnkck1n4" />
    </div>;
}
function LockedStudyState({
  programId,
  programName
}: {
  programId: 'rd-ready-rd-exam' | 'rd-ready-cnsc-prep';
  programName: string;
}) {
  return <div className="min-h-screen bg-white px-5 py-20 md:px-8" style={{
    fontFamily: FONT
  }} data-id="element-2937" data-mp="2pdgtx6k05nbw">
      <div className="mx-auto max-w-2xl rounded-3xl border border-[#E2E8F0] bg-white p-8 text-center shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-12" data-id="element-2938" data-mp="2cxphchvtdycm">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{
        backgroundColor: '#EEF4FF',
        color: BLUE
      }} data-id="element-2939" data-mp="2rtoyei0wj8tw">
          <LockKeyholeIcon className="h-6 w-6" data-id="element-2940" data-mp="1z3oqsbf0nhnc" />
        </span>
        <p className="mt-7 text-[10px] font-bold uppercase tracking-[0.2em]" style={{
        color: BLUE
      }} data-id="element-2941" data-mp="1u7r9a1s0o58z">
          Purchase required
        </p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A] md:text-4xl" data-id="element-2942" data-mp="243uni6057uca">
          Unlock {programName} to start studying.
        </h1>
        <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2943" data-mp="1bg88m1lgt3a4">
          This program is a one-time $150 purchase. Access is granted only to
          the account with a paid order for this exact study program.
        </p>
        <Link to={getCheckoutUrl(programId)} className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5" style={{
        backgroundColor: BLUE
      }} data-id="element-2944" data-mp="254w8y972wpgl">
          Unlock for $150 <ArrowRightIcon className="h-4 w-4" data-id="element-2945" data-mp="1i72qfixmdyak" />
        </Link>
        <p className="mt-7 text-xs leading-relaxed text-[#94A3B8]" data-id="element-2946" data-mp="1m6jz0l8cfbjy">
          Prototype note: Firestore rules and server-side entitlement checks
          must secure study content in production.
        </p>
      </div>
    </div>;
}
function InvalidStudyProgramState() {
  return <div className="flex min-h-[70vh] items-center justify-center bg-white px-5 text-center" data-id="element-2947" data-mp="32llgmjipmlxw">
      <div className="max-w-md rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-[0_4px_28px_rgba(15,23,42,.06)]" data-id="element-2948" data-mp="37kg0zpwbsik4">
        <LockKeyholeIcon className="mx-auto h-8 w-8 text-[#2563EB]" data-id="element-2949" data-mp="vxceu44nn5nk" />
        <h1 className="mt-4 text-2xl font-bold text-[#0F172A]" data-id="element-2950" data-mp="qxztjntfm6n3">
          Select a study program
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-2951" data-mp="m6zzcqbp62oa">
          Choose RD Exam Prep or CNSC Exam Prep to review access and begin
          studying.
        </p>
        <Link to="/" className="mt-6 inline-flex rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white" data-id="element-2952" data-mp="h6c8q5rdrnul">
          Explore programs
        </Link>
      </div>
    </div>;
}
function Dashboard({
  onStartQuiz,
  onStartCards
}: {
  onStartQuiz: () => void;
  onStartCards: () => void;
}) {
  return <div className="grid gap-6 lg:grid-cols-[1.45fr_.8fr]" data-id="element-2953" data-mp="3ufi2jykxxjp3">
      <section data-id="element-2954" data-mp="3uh2wpn3cf0mf">
        <div className="rounded-3xl p-7 text-white md:p-9" style={{
        backgroundColor: BLUE
      }} data-id="element-2955" data-mp="1xm6ednsxez1c">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/70" data-id="element-2956" data-mp="1snv6vjq3t0ma">
            Your next step
          </p>
          <h2 className="mt-3 text-2xl font-bold md:text-3xl" data-id="element-2957" data-mp="11kc0mbd5rhnq">
            Build confidence in parenteral nutrition.
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85" data-id="element-2958" data-mp="sq7fm8kph0g4">
            Your accuracy suggests a short practice set followed by a flashcard
            review.
          </p>
          <div className="mt-7 flex flex-wrap gap-3" data-id="element-2959" data-mp="irrpa7ti5v7o">
            <button type="button" onClick={onStartQuiz} className="rounded-full bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-transform hover:-translate-y-0.5" style={{
            color: BLUE
          }} data-id="element-2960" data-mp="iq6sscnmo276">
              Start a quiz
            </button>
            <button type="button" onClick={onStartCards} className="rounded-full border border-white/50 px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-white/10" data-id="element-2961" data-mp="245w1sxq4xvfv">
              Review cards
            </button>
          </div>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-2962" data-mp="3oksk5mim7kc8">
          <StudyCard icon={BookOpenCheckIcon} label="Questions completed" value="42" note="This week" data-id="element-2963" data-mp="69xl4uoik51k" />
          <StudyCard icon={BoxIcon} label="Flashcards reviewed" value="128" note="This week" data-id="element-2964" data-mp="g6tmmgfzvqwm" />
        </div>
        <section className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#F7F9FC] p-6" data-id="element-2965" data-mp="b7pgjyoda9ai">
          <div className="flex items-center justify-between" data-id="element-2966" data-mp="p78btqw2xq5e">
            <h2 className="text-xl font-bold text-[#0F172A]" data-id="element-2967" data-mp="k8f6wmuuz7gu">Topic progress</h2>
            <span className="text-xs font-semibold" style={{
            color: BLUE
          }} data-id="element-2968" data-mp="2qyv3zvrpjr3g">
              Updated today
            </span>
          </div>
          <div className="mt-6 space-y-5" data-id="element-2969" data-mp="1ub1esyn0r83t">
            {topics.map(([topic, progress]) => <TopicProgress key={topic} topic={topic} progress={progress as number} data-id="element-2970" data-mp="1pczy2kd2fkvi" />)}
          </div>
        </section>
      </section>
      <aside className="rounded-2xl border border-[#E2E8F0] bg-[#F0FBF9] p-6" data-id="element-2971" data-mp="1z21c4501vs6u">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{
        color: TEAL
      }} data-id="element-2972" data-mp="1bcfdpjri5gzw">
          Study rhythm
        </p>
        <div className="mt-5 border-b border-[#D5EFEA] pb-6" data-id="element-2973" data-mp="16fyo0l4nmukp">
          <p className="text-5xl font-extrabold text-[#0F172A]" data-id="element-2974" data-mp="1keygk3vaftm5">4</p>
          <p className="mt-1 text-sm text-[#5A6B7B]" data-id="element-2975" data-mp="1fe0ydraa2zkn">active days this week</p>
        </div>
        <div className="space-y-4 py-6 text-sm text-[#334155]" data-id="element-2976" data-mp="2vs0uss9x0735">
          <p className="flex gap-3" data-id="element-2977" data-mp="2vudbl23xsbem">
            <CheckIcon className="h-4 w-4 shrink-0" style={{
            color: TEAL
          }} strokeWidth={3} data-id="element-2978" data-mp="2qtz53ohzh9ip" />
            Keep sessions short and targeted.
          </p>
          <p className="flex gap-3" data-id="element-2979" data-mp="33dmi8nl25vzz">
            <CheckIcon className="h-4 w-4 shrink-0" style={{
            color: TEAL
          }} strokeWidth={3} data-id="element-2980" data-mp="2pca7qroo2mlh" />
            Use misses to choose the next topic.
          </p>
        </div>
        <p className="border-t border-[#D5EFEA] pt-5 text-xs leading-relaxed text-[#94A3B8]" data-id="element-2981" data-mp="3i1hfmha0goje">
          Content is representative study material for learning support and does
          not reproduce official exam content.
        </p>
      </aside>
    </div>;
}
function StudyCard({
  icon: Icon,
  label,
  value,
  note
}: {
  icon: typeof BookOpenCheckIcon;
  label: string;
  value: string;
  note: string;
}) {
  return <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-[0_2px_16px_rgba(15,23,42,.05)]" data-id="element-2982" data-mp="4scp5sfkmg67">
      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{
      backgroundColor: '#EEF4FF'
    }} data-id="element-2983" data-mp="9myylq23dynn">
        <Icon className="h-5 w-5" style={{
        color: BLUE
      }} data-id="element-2984" data-mp="eikzcwqw08xx" />
      </span>
      <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.15em] text-[#94A3B8]" data-id="element-2985" data-mp="9l1jxtigdq4e">
        {label}
      </p>
      <p className="mt-1 text-3xl font-extrabold text-[#0F172A]" data-id="element-2986" data-mp="ia6xp9ximan">{value}</p>
      <p className="mt-1 text-xs text-[#94A3B8]" data-id="element-2987" data-mp="kd54wl8jlqg">{note}</p>
    </div>;
}
function TopicProgress({
  topic,
  progress
}: {
  topic: string;
  progress: number;
}) {
  return <div data-id="element-2988" data-mp="3myef39de9gdx">
      <div className="mb-2 flex justify-between text-sm" data-id="element-2989" data-mp="3my4objkiz576">
        <span className="font-medium text-[#334155]" data-id="element-2990" data-mp="3hzjmxgtjefh5">{topic}</span>
        <span className="text-[#94A3B8]" data-id="element-2991" data-mp="3dx6tnrvz8a19">{progress}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#E2E8F0]" data-id="element-2992" data-mp="3dwdjegxt72ed">
        <div className="h-full rounded-full" style={{
        width: `${progress}%`,
        backgroundColor: BLUE
      }} data-id="element-2993" data-mp="38z4yi8la0k9f" />
      </div>
    </div>;
}
function QuizSectionChooser({
  sections,
  selectedIds,
  onBack,
  onToggle,
  onStart
}: {
  sections: QuizSection[];
  selectedIds: string[];
  onBack: () => void;
  onToggle: (sectionId: string) => void;
  onStart: (settings: QuizSettings) => void;
}) {
  const [setupOpen, setSetupOpen] = useState(false);
  const selectedSections = sections.filter(section => selectedIds.includes(section.id));
  const totalQuestions = selectedSections.reduce((total, section) => total + section.questionCount, 0);
  return <section className="mx-auto max-w-5xl" data-id="element-2994" data-mp="1eoond69j4csj">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B] transition-colors hover:text-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-2995" data-mp="1jgom4vdv4znj">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-2996" data-mp="2tmja3m3u4apm" /> Study overview
      </button>
      <div className="mt-5 rounded-3xl border border-[#E2E8F0] bg-[#F7F9FC] p-6 md:p-9" data-id="element-2997" data-mp="2oohoatf0xxag">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-2998" data-mp="32fmivh3jbzdx">
          Practice quiz
        </p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-2999" data-mp="2xoeb0phusm2v">
          Choose your material sections
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3000" data-mp="3cc91dz8e2ecs">
          Select one section for a focused quiz, or combine multiple sections
          for broader review. Scores below are representative past results.
        </p>

        <fieldset className="mt-8" data-id="element-3001" data-mp="3hd6i07dr30c6">
          <legend className="sr-only" data-id="element-3002" data-mp="3he85jbbydfas">Choose quiz material sections</legend>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3003" data-mp="1qzxx2t20p6ep">
            {sections.map(section => {
            const isSelected = selectedIds.includes(section.id);
            return <label key={section.id} className={`group relative flex min-h-[216px] cursor-pointer flex-col rounded-2xl border bg-white p-5 transition-colors ${isSelected ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] hover:border-[#93C5FD]'} focus-within:ring-2 focus-within:ring-[#2563EB] focus-within:ring-offset-2`} data-id="element-3004" data-mp="1cup42vcjp09c">
                  <input type="checkbox" checked={isSelected} onChange={() => onToggle(section.id)} className="peer sr-only" data-id="element-3005" data-mp="t781nfhx3xs7" />
                  <span aria-hidden="true" className={`flex h-6 w-6 items-center justify-center rounded-md border ${isSelected ? 'border-[#2563EB] bg-[#2563EB] text-white' : 'border-[#CBD5E1] bg-white text-transparent'}`} data-id="element-3006" data-mp="k7bexjf398zs">
                    <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-3007" data-mp="2jjxcfza8s7m7" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold text-[#0F172A]" data-id="element-3008" data-mp="2h58qkl2h1995">
                    {section.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3009" data-mp="2d2cfdff9gff8">
                    {section.description}
                  </p>
                  <span className="mt-auto border-t border-[#E2E8F0] pt-4 text-xs font-semibold text-[#0F766E]" data-id="element-3010" data-mp="285vnqb8urixe">
                    Past score: {section.pastScore}
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        <div aria-live="polite" className="mt-7 flex flex-col gap-4 rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-3011" data-mp="2vuexkv7jw3rl">
          <div data-id="element-3012" data-mp="2m3k0swwohlrw">
            <p className="text-sm font-bold text-[#0F172A]" data-id="element-3013" data-mp="2m1zfzz061h78">
              {selectedIds.length}{' '}
              {selectedIds.length === 1 ? 'section' : 'sections'} selected
            </p>
            <p className="mt-1 text-xs text-[#52627E]" data-id="element-3014" data-mp="3eqeijm24271o">
              {selectedIds.length ? `${totalQuestions} representative questions in your selected scope.` : 'Select at least one section to start your quiz.'}
            </p>
          </div>
          <button type="button" disabled={!selectedIds.length} onClick={() => setSetupOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0" data-id="element-3015" data-mp="3w08okx6qd070">
            Start quiz <ArrowRightIcon className="h-4 w-4" data-id="element-3016" data-mp="sl34jjjecxrk" />
          </button>
        </div>
      </div>
      {setupOpen && <QuizSetupDialog selectedSectionCount={selectedIds.length} onClose={() => setSetupOpen(false)} onStart={settings => {
      setSetupOpen(false);
      onStart(settings);
    }} data-id="element-3017" data-mp="wm6oah4kfdy2" />}
    </section>;
}
function QuizSetupDialog({
  selectedSectionCount,
  onClose,
  onStart
}: {
  selectedSectionCount: number;
  onClose: () => void;
  onStart: (settings: QuizSettings) => void;
}) {
  const [questionCount, setQuestionCount] = useState(10);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | null>(null);
  const [revealIncorrectAnswers, setRevealIncorrectAnswers] = useState(true);
  const [formats, setFormats] = useState<QuestionFormat[]>(['multiple-choice']);
  const toggleFormat = (format: QuestionFormat) => {
    setFormats(current => current.includes(format) ? current.filter(value => value !== format) : [...current, format]);
  };
  const formatOptions: {
    id: QuestionFormat;
    label: string;
    note: string;
  }[] = [{
    id: 'multiple-choice',
    label: 'Multiple choice',
    note: 'Choose one answer'
  }, {
    id: 'open-ended',
    label: 'Open ended',
    note: 'Write your response'
  }, {
    id: 'matching',
    label: 'Matching',
    note: 'Pair related concepts'
  }, {
    id: 'true-false',
    label: 'True or false',
    note: 'Quick recall check'
  }];
  return <div className="fixed inset-0 z-[70] flex items-end bg-[#0F172A]/45 p-4 sm:items-center sm:justify-center" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }} data-id="element-3018" data-mp="2i3qnv9h8o4bk">
      <section role="dialog" aria-modal="true" aria-labelledby="quiz-setup-title" className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8" data-id="element-3019" data-mp="2wto8xgjvgdwj">
        <div className="flex items-start justify-between gap-5" data-id="element-3020" data-mp="1ldznitm9nmxu">
          <div data-id="element-3021" data-mp="1qexdvthfjnaf">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-3022" data-mp="1qff7eicpjnen">
              Quiz setup
            </p>
            <h2 id="quiz-setup-title" className="mt-2 text-2xl font-extrabold text-[#0F172A]" data-id="element-3023" data-mp="ojhzgsx75ac3">
              Tailor this practice session
            </h2>
            <p className="mt-2 text-sm text-[#5A6B7B]" data-id="element-3024" data-mp="ydgyvk5vyg0c">
              {selectedSectionCount} selected{' '}
              {selectedSectionCount === 1 ? 'section' : 'sections'} will shape
              your quiz scope.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#0F172A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]" aria-label="Close quiz setup" data-id="element-3025" data-mp="ap7jazz9mohn">
            <XIcon className="h-5 w-5" data-id="element-3026" data-mp="3j4r91p0q9ory" />
          </button>
        </div>

        <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-3027" data-mp="3n75rw94ecoj8">
          <label className="text-sm font-semibold text-[#334155]" data-id="element-3028" data-mp="3n5ky4lpwp9mf">
            Number of questions
            <select value={questionCount} onChange={event => setQuestionCount(Number(event.target.value))} className={fieldClass} data-id="element-3029" data-mp="3fn3gttbsgqiw">
              {[5, 10, 15, 20].map(count => <option key={count} value={count} data-id="element-3030" data-mp="31odjqdoyyquj">
                  {count} questions
                </option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-[#334155]" data-id="element-3031" data-mp="2wr3nae35xex7">
            Time limit
            <select value={timeLimitMinutes ?? 'none'} onChange={event => setTimeLimitMinutes(event.target.value === 'none' ? null : Number(event.target.value))} className={fieldClass} data-id="element-3032" data-mp="2rwzhhy5mvcxp">
              <option value="none" data-id="element-3033" data-mp="28ywnf4y8apt1">No time limit</option>
              <option value="10" data-id="element-3034" data-mp="291j2rqrd29lt">10 minutes</option>
              <option value="20" data-id="element-3035" data-mp="1zbki68x6glz9">20 minutes</option>
              <option value="30" data-id="element-3036" data-mp="1ze6roe3mpbk4">30 minutes</option>
            </select>
          </label>
        </div>

        <label className="mt-6 flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-5 focus-within:ring-2 focus-within:ring-[#2563EB] focus-within:ring-offset-2" data-id="element-3037" data-mp="248kh7zvm0ogv">
          <span data-id="element-3038" data-mp="2i2lib0a1anke">
            <span className="block text-sm font-bold text-[#0F172A]" data-id="element-3039" data-mp="2i21y1cwwvn37">
              Reveal incorrect answers immediately
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-[#52627E]" data-id="element-3040" data-mp="2n0mztgrtaetr">
              Show the correct answer and rationale after an incorrect choice.
            </span>
          </span>
          <input type="checkbox" checked={revealIncorrectAnswers} onChange={event => setRevealIncorrectAnswers(event.target.checked)} className="h-5 w-5 rounded border-[#94A3B8] text-[#2563EB] focus:ring-[#2563EB]" data-id="element-3041" data-mp="2da0nbvfe64fa" />
        </label>

        <fieldset className="mt-6" data-id="element-3042" data-mp="uakohwgdlw92">
          <legend className="text-sm font-bold text-[#334155]" data-id="element-3043" data-mp="pecboppnif52">
            Question formats
          </legend>
          <p className="mt-1 text-xs text-[#64748B]" data-id="element-3044" data-mp="141p2oty89d2u">
            Choose at least one format for this session.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-3045" data-mp="z45plxd9n6ch">
            {formatOptions.map(format => {
            const checked = formats.includes(format.id);
            return <label key={format.id} className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${checked ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] bg-white'}`} data-id="element-3046" data-mp="6g9swsnp5efp">
                  <input type="checkbox" checked={checked} onChange={() => toggleFormat(format.id)} className="mt-0.5 h-4 w-4 rounded border-[#94A3B8] text-[#2563EB] focus:ring-[#2563EB]" data-id="element-3047" data-mp="1vtwmu4cxz7lz" />
                  <span data-id="element-3048" data-mp="2n4ubz6rg39tw">
                    <span className="block text-sm font-semibold text-[#0F172A]" data-id="element-3049" data-mp="2r3jn0sp0gfad">
                      {format.label}
                    </span>
                    <span className="mt-1 block text-xs text-[#64748B]" data-id="element-3050" data-mp="2w1uuwxvqwfpy">
                      {format.note}
                    </span>
                  </span>
                </label>;
          })}
          </div>
        </fieldset>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end" data-id="element-3051" data-mp="3a2p7lsptt2uf">
          <button type="button" onClick={onClose} className="rounded-full border border-[#CBD5E1] px-6 py-3 text-sm font-semibold text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]" data-id="element-3052" data-mp="3etnnir6wjyx5">
            Cancel
          </button>
          <button type="button" disabled={!formats.length} onClick={() => onStart({
          questionCount,
          timeLimitMinutes,
          revealIncorrectAnswers,
          formats
        })} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-3053" data-mp="3tqxxfw3wcwqr">
            Start quiz <ArrowRightIcon className="h-4 w-4" data-id="element-3054" data-mp="1vw0y2f9vwp2m" />
          </button>
        </div>
      </section>
    </div>;
}
type GeneratedQuizQuestion = {
  format: QuestionFormat;
  prompt: string;
  options?: string[];
  answer: string;
  rationale: string;
  matchTerm?: string;
};
function Quiz({
  selectedSections,
  settings,
  onBack,
  onFinish
}: {
  selectedSections: QuizSection[];
  settings: QuizSettings;
  onBack: () => void;
  onFinish: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [response, setResponse] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(settings.timeLimitMinutes ? settings.timeLimitMinutes * 60 : null);
  const quizQuestions = Array.from({
    length: settings.questionCount
  }, (_, questionIndex) => makeQuizQuestion(settings.formats[questionIndex % settings.formats.length], questionIndex));
  useEffect(() => {
    if (secondsRemaining === null) return;
    if (secondsRemaining <= 0) {
      onFinish();
      return;
    }
    const timer = window.setInterval(() => setSecondsRemaining(current => current === null ? null : current - 1), 1000);
    return () => window.clearInterval(timer);
  }, [onFinish, secondsRemaining]);
  const question = quizQuestions[index];
  const isLast = index === quizQuestions.length - 1;
  const normalizedResponse = response.trim().toLowerCase();
  const normalizedAnswer = question.answer.toLowerCase();
  const isCorrect = question.format === 'open-ended' ? normalizedResponse.includes(normalizedAnswer) : normalizedResponse === normalizedAnswer;
  const scopeLabel = selectedSections.map(section => section.title).join(' · ');
  const formatLabel = formatQuizLabel(question.format);
  const checkAnswer = () => {
    if (!response.trim()) return;
    if (!isCorrect && settings.revealIncorrectAnswers) setRevealed(true);else setRevealed(true);
  };
  const next = () => {
    if (isLast) {
      onFinish();
      return;
    }
    setIndex(value => value + 1);
    setResponse('');
    setRevealed(false);
  };
  const chooseResponse = (value: string) => {
    if (revealed) return;
    setResponse(value);
    if (settings.revealIncorrectAnswers && !isCorrectResponse(value, question)) {
      setRevealed(true);
    }
  };
  return <section className="mx-auto max-w-3xl rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-9" data-id="element-3055" data-mp="1xfx2f2vcy3ry">
      <div className="flex items-start justify-between gap-4 text-xs font-bold uppercase tracking-[0.14em] text-[#94A3B8]" data-id="element-3056" data-mp="1gk9jxyserqr0">
        <div data-id="element-3057" data-mp="1laq1nv39hyj6">
          <span className="block" data-id="element-3058" data-mp="1lcav1hdubbfg">Practice quiz · {formatLabel}</span>
          <span className="mt-2 block max-w-md normal-case tracking-normal text-[#0F766E]" data-id="element-3059" data-mp="1qaks5xj4km3p">
            {scopeLabel}
          </span>
        </div>
        <div className="shrink-0 text-right" data-id="element-3060" data-mp="so59wtivjkf3">
          {secondsRemaining !== null && <span className="mb-1 block text-[#0F766E]" data-id="element-3061" data-mp="xlxcchh5aoxj">
              {formatQuizTime(secondsRemaining)}
            </span>}
          <span data-id="element-3062" data-mp="12muq8hv4gwt8">
            {index + 1} / {quizQuestions.length}
          </span>
        </div>
      </div>
      <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-[#E2E8F0]" data-id="element-3063" data-mp="2dznidtrtrn9t">
        <div className="h-full rounded-full transition-all" style={{
        width: `${(index + 1) / quizQuestions.length * 100}%`,
        backgroundColor: BLUE
      }} data-id="element-3064" data-mp="2e1q5o61lf2au" />
      </div>
      <h1 className="mt-9 text-2xl font-bold leading-snug text-[#0F172A] md:text-3xl" data-id="element-3065" data-mp="2ck1onixb2kn">
        {question.prompt}
      </h1>

      <div className="mt-8" data-id="element-3066" data-mp="3tpxul9yu1k5f">
        {(question.format === 'multiple-choice' || question.format === 'true-false') && <div className="space-y-3" role="radiogroup" aria-label={formatLabel} data-id="element-3067" data-mp="3orml50cg9bnq">
            {question.options?.map(option => <button type="button" key={option} role="radio" aria-checked={response === option} disabled={revealed} onClick={() => chooseResponse(option)} className={`w-full rounded-xl border p-4 text-left text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 ${revealed && option === question.answer ? 'border-[#0F9E8E] bg-[#F0FBF9]' : response === option ? 'border-[#2563EB] bg-[#F5F8FF]' : 'border-[#E2E8F0] hover:border-[#2563EB]'}`} data-id="element-3068" data-mp="3etoyuie5hoxr">
                {option}
              </button>)}
          </div>}
        {question.format === 'open-ended' && <label className="block text-sm font-semibold text-[#334155]" data-id="element-3069" data-mp="8714jbz1tqmn">
            Your response
            <textarea rows={4} value={response} disabled={revealed} onChange={event => setResponse(event.target.value)} placeholder="Write your answer in your own words" className={`${fieldClass} resize-y disabled:bg-[#F8FAFC]`} data-id="element-3070" data-mp="478v6x1tzgg8" />
          </label>}
        {question.format === 'matching' && <label className="block text-sm font-semibold text-[#334155]" data-id="element-3071" data-mp="n1xo2lcb60f1">
            Match “{question.matchTerm}” with the correct description
            <select value={response} disabled={revealed} onChange={event => chooseResponse(event.target.value)} className={`${fieldClass} disabled:bg-[#F8FAFC]`} data-id="element-3072" data-mp="1euodabvkqous">
              <option value="" data-id="element-3073" data-mp="32hkk0btuekap">Choose a match</option>
              {question.options?.map(option => <option key={option} value={option} data-id="element-3074" data-mp="36hb6i3cc6e9f">
                  {option}
                </option>)}
            </select>
          </label>}
      </div>

      {revealed && <div className={`mt-6 rounded-r-xl border-l-4 p-4 ${isCorrect ? 'border-[#0F9E8E] bg-[#F0FBF9]' : 'border-[#2563EB] bg-[#F5F8FF]'}`} data-id="element-3075" data-mp="3ggjywrrjzkto">
          <p className="text-xs font-bold uppercase tracking-[0.15em]" style={{
        color: isCorrect ? TEAL : BLUE
      }} data-id="element-3076" data-mp="3q6dc1x7cg801">
            {isCorrect ? 'Correct' : `Correct answer: ${question.answer}`}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[#334155]" data-id="element-3077" data-mp="8109k3u06mjw">
            {question.rationale}
          </p>
        </div>}

      <div className="mt-8 flex justify-between gap-4" data-id="element-3078" data-mp="2456leek0r1ob">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.12em] text-[#94A3B8] transition-colors hover:text-[#0F172A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-3079" data-mp="1z525apn0w8ym">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3080" data-mp="2mtv4qw2usyxz" /> Change sections
        </button>
        <button type="button" disabled={!response.trim()} onClick={() => revealed ? next() : checkAnswer()} className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 disabled:opacity-40 disabled:hover:translate-y-0" style={{
        backgroundColor: BLUE
      }} data-id="element-3081" data-mp="2itb6tqaghras">
          {revealed ? isLast ? 'Finish quiz' : 'Next question' : 'Check answer'}{' '}
          <ChevronRightIcon className="h-4 w-4" data-id="element-3082" data-mp="3e3zbiscbn71l" />
        </button>
      </div>
    </section>;
}
function makeQuizQuestion(format: QuestionFormat, index: number): GeneratedQuizQuestion {
  const sampleIndex = index % 2;
  if (format === 'multiple-choice') {
    const item = questions[sampleIndex];
    return {
      format,
      prompt: item.prompt,
      options: item.answers,
      answer: item.answers[item.correct],
      rationale: item.rationale
    };
  }
  if (format === 'true-false') {
    return sampleIndex === 0 ? {
      format,
      prompt: 'True or false: Enteral nutrition is generally preferred when the GI tract is functional and clinically appropriate.',
      options: ['True', 'False'],
      answer: 'True',
      rationale: 'When feasible, enteral nutrition supports use of the GI tract and is generally preferred.'
    } : {
      format,
      prompt: 'True or false: A single gastric residual volume should be used alone to determine enteral feeding tolerance.',
      options: ['True', 'False'],
      answer: 'False',
      rationale: 'Tolerance is assessed from the broader clinical pattern, including intake, GI findings, and clinical status.'
    };
  }
  if (format === 'matching') {
    return sampleIndex === 0 ? {
      format,
      prompt: 'Match the nutrition support concept to its definition.',
      matchTerm: 'Refeeding risk',
      options: ['Need for cautious calorie advancement with electrolyte monitoring', 'A formula delivery route through the GI tract', 'A rapid nutrition screening tool'],
      answer: 'Need for cautious calorie advancement with electrolyte monitoring',
      rationale: 'Refeeding risk requires cautious advancement and close electrolyte monitoring.'
    } : {
      format,
      prompt: 'Match the assessment concept to its definition.',
      matchTerm: 'Nutrition screening',
      options: ['A rapid process to identify nutrition risk', 'A complete nutrition diagnosis', 'A method for calculating fluid needs'],
      answer: 'A rapid process to identify nutrition risk',
      rationale: 'Screening rapidly identifies people who may need comprehensive assessment.'
    };
  }
  return sampleIndex === 0 ? {
    format,
    prompt: 'In a few words, what should be monitored when advancing nutrition in someone at refeeding risk?',
    answer: 'electrolytes',
    rationale: 'Electrolytes should be monitored closely and repleted as clinically indicated during cautious advancement.'
  } : {
    format,
    prompt: 'In a few words, what is the purpose of nutrition screening?',
    answer: 'risk',
    rationale: 'Nutrition screening identifies people at nutrition risk who may need further assessment.'
  };
}
function formatQuizLabel(format: QuestionFormat) {
  return format === 'multiple-choice' ? 'Multiple choice' : format === 'open-ended' ? 'Open ended' : format === 'matching' ? 'Matching' : 'True or false';
}
function isCorrectResponse(value: string, question: GeneratedQuizQuestion) {
  const response = value.trim().toLowerCase();
  const answer = question.answer.toLowerCase();
  return question.format === 'open-ended' ? response.includes(answer) : response === answer;
}
function formatQuizTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
function FlashcardLibrary({
  builtInSets,
  customSets,
  customSetsLoading,
  customSetsError,
  onCreate,
  onSelect
}: {
  builtInSets: FlashcardSet[];
  customSets: FlashcardSet[];
  customSetsLoading: boolean;
  customSetsError: string | null;
  onCreate: () => void;
  onSelect: (set: FlashcardSet) => void;
}) {
  return <section className="mx-auto max-w-5xl" data-id="element-3083" data-mp="udk33cw7neyb">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-3084" data-mp="uelp2cerllut">
        Flashcard library
      </p>
      <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-3085" data-mp="1i60lxpnrvayo">
        Choose a set to review
      </h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3086" data-mp="19bm71o75m2g3">
        Focus on one high-yield area at a time with active recall practice.
      </p>

      <div className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-3087" data-mp="1db4r6hr5i9hv">
        <button type="button" onClick={onCreate} className="rounded-2xl border-2 border-dashed border-[#93C5FD] bg-[#F5F8FF] p-6 text-left transition-colors hover:border-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-3088" data-mp="212a6mvt6izrn">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#2563EB] shadow-sm" data-id="element-3089" data-mp="1cb4ci6u77eph">
            <PlusIcon className="h-5 w-5" data-id="element-3090" data-mp="17d2rhgd7p5bd" />
          </span>
          <h3 className="mt-6 text-xl font-bold text-[#0F172A]" data-id="element-3091" data-mp="12ehtlnlfmxq3">
            Create your own set
          </h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-[#52627E]" data-id="element-3092" data-mp="2005o22ybv5yz">
            Turn your notes, missed questions, and study reminders into private
            flashcards that stay with your account.
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-3093" data-mp="1q1g6kbacfnj9">
            Build a set <ArrowRightIcon className="h-4 w-4" data-id="element-3094" data-mp="1l6ka8yy2qe0c" />
          </span>
        </button>

        <aside className="rounded-2xl border border-[#C7EFE8] bg-[#F0FBF9] p-6" data-id="element-3095" data-mp="epfcqhymh8wn">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0F766E]" data-id="element-3096" data-mp="9nhkiy88v3rk">
            Your account, your notes
          </p>
          <h3 className="mt-4 text-xl font-bold text-[#0F172A]" data-id="element-3097" data-mp="2qbh6ald8hgnq">
            Saved securely for you
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-[#52627E]" data-id="element-3098" data-mp="2vau0jr6fpq0c">
            Your personal decks sync to your signed-in RD Ready account, so they
            are available after you sign out or switch devices.
          </p>
        </aside>
      </div>

      <div className="mt-10" data-id="element-3099" data-mp="3503yhnnym2wd">
        <div className="flex items-center justify-between gap-4" data-id="element-3100" data-mp="391yz0py2gc93">
          <div data-id="element-3101" data-mp="3905pn2w2jdxe">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0F9E8E]" data-id="element-3102" data-mp="393tjy49c84d6">
              Your sets
            </p>
            <h3 className="mt-2 text-xl font-bold text-[#0F172A]" data-id="element-3103" data-mp="3j0oekc2hpkm6">
              Personal flashcards
            </h3>
          </div>
          {!customSetsLoading && <span className="text-xs font-medium text-[#64748B]" data-id="element-3104" data-mp="1jpdgxkxnv9ll">
              {customSets.length} {customSets.length === 1 ? 'set' : 'sets'}
            </span>}
        </div>

        {customSetsLoading ? <div className="mt-5 flex items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-6 text-sm text-[#64748B]" data-id="element-3105" data-mp="1eydrnkgw6jl7">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-3106" data-mp="10b2drbx8mnhx" />
            Loading your saved sets…
          </div> : customSetsError ? <p role="alert" className="mt-5 rounded-2xl border border-[#FBD5D0] bg-[#FEF2F2] p-5 text-sm leading-relaxed text-[#B91C1C]" data-id="element-3107" data-mp="1szg9tm4rn9qu">
            {customSetsError}
          </p> : customSets.length ? <div className="mt-5 grid gap-5 md:grid-cols-3" data-id="element-3108" data-mp="1j2lipoatobml">
            {customSets.map(set => <FlashcardSetCard key={set.id} set={set} onSelect={onSelect} data-id="element-3109" data-mp="7l25r47fwuau" />)}
          </div> : <div className="mt-5 rounded-2xl border border-dashed border-[#A7DCD5] bg-[#F8FCFB] p-6 text-sm leading-relaxed text-[#52627E]" data-id="element-3110" data-mp="ckoilcjcw8j4">
            You have not created a set yet. Add cards from your notes and they
            will appear here whenever you return.
          </div>}
      </div>

      <div className="mt-10" data-id="element-3111" data-mp="2zt4dju6c7c1q">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#64748B]" data-id="element-3112" data-mp="2zrb3e4wim9oz">
          Built-in decks
        </p>
        <h3 className="mt-2 text-xl font-bold text-[#0F172A]" data-id="element-3113" data-mp="2vowm3p8hvhzt">
          RD Ready study sets
        </h3>
        <div className="mt-5 grid gap-5 md:grid-cols-3" data-id="element-3114" data-mp="27z2atc3tq1ho">
          {builtInSets.map(set => <FlashcardSetCard key={set.id} set={set} onSelect={onSelect} data-id="element-3115" data-mp="230inp8dhujnq" />)}
        </div>
      </div>
    </section>;
}
function FlashcardSetCard({
  set,
  onSelect
}: {
  set: FlashcardSet;
  onSelect: (set: FlashcardSet) => void;
}) {
  return <button type="button" onClick={() => onSelect(set)} className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-left shadow-[0_2px_16px_rgba(15,23,42,.04)] transition-colors hover:border-[#2563EB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-3116" data-mp="1oc4nz6bdjbbe">
      <BookOpenCheckIcon className={`h-7 w-7 ${set.isCustom ? 'text-[#0F9E8E]' : 'text-[#2563EB]'}`} data-id="element-3117" data-mp="3odm1zi0sve3j" />
      <div className="mt-5 flex items-center justify-between gap-3" data-id="element-3118" data-mp="3t8rp2k5xuyt7">
        <h4 className="text-xl font-bold text-[#0F172A]" data-id="element-3119" data-mp="1zkpoyl657cw">{set.title}</h4>
        {set.isCustom && <span className="shrink-0 rounded-full bg-[#F0FBF9] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-[#0F766E]" data-id="element-3120" data-mp="30jkpdhur036d">
            Yours
          </span>}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3121" data-mp="3aecnqnw5mu27">
        {set.description}
      </p>
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB]" data-id="element-3122" data-mp="3fglf5ftdpo27">
        {set.cardCount} {set.cardCount === 1 ? 'card' : 'cards'}{' '}
        <ArrowRightIcon className="h-4 w-4" data-id="element-3123" data-mp="uo035gonjhm4" />
      </span>
    </button>;
}
function CreateFlashcardSet({
  onCancel,
  onSave
}: {
  onCancel: () => void;
  onSave: (set: CustomFlashcardSet) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [cards, setCards] = useState([{
    topic: '',
    front: '',
    back: ''
  }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const updateCard = (index: number, field: 'topic' | 'front' | 'back', value: string) => {
    setCards(current => current.map((card, cardIndex) => cardIndex === index ? {
      ...card,
      [field]: value
    } : card));
  };
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    const cleanTitle = title.trim();
    const cleanDescription = description.trim();
    const cleanCards = cards.map(card => ({
      topic: card.topic.trim(),
      front: card.front.trim(),
      back: card.back.trim()
    })).filter(card => card.front || card.back);
    if (!cleanTitle) {
      setError('Give your flashcard set a name before saving.');
      return;
    }
    if (!cleanDescription) {
      setError('Add a short description to help you recognize this set.');
      return;
    }
    if (!cleanCards.length) {
      setError('Add at least one prompt and answer card before saving.');
      return;
    }
    if (cleanCards.some(card => !card.front || !card.back)) {
      setError('Each card needs both a prompt and an answer.');
      return;
    }
    const now = new Date().toISOString();
    setSaving(true);
    try {
      await onSave({
        id: `custom-${now.replace(/[^0-9]/g, '')}-${Math.random().toString(36).slice(2, 7)}`,
        title: cleanTitle,
        description: cleanDescription,
        cards: cleanCards.map(card => {
          const cleanCard: {
            front: string;
            back: string;
            topic?: string;
          } = {
            front: card.front,
            back: card.back
          };
          if (card.topic) cleanCard.topic = card.topic;
          return cleanCard;
        }),
        createdAt: now,
        updatedAt: now
      });
      setSuccess('Your set has been saved to your account.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'We could not save your set. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  return <section className="mx-auto max-w-3xl" data-id="element-3124" data-mp="1l0wh3s1lzhab">
      <button type="button" onClick={onCancel} disabled={saving} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B] transition-colors hover:text-[#2563EB] disabled:opacity-60" data-id="element-3125" data-mp="1fzfiy4aie8xf">
        <ChevronLeftIcon className="h-4 w-4" data-id="element-3126" data-mp="1x13a950kbog4" /> All sets
      </button>
      <div className="mt-5 rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-[0_4px_28px_rgba(15,23,42,.06)] md:p-9" data-id="element-3127" data-mp="1x2wfcfvj20m0">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-3128" data-mp="26z99tfywciht">
          Your private library
        </p>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A]" data-id="element-3129" data-mp="2bq956gsdasi7">
          Create your own set
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-3130" data-mp="2goj1wvtqq15x">
          Build a focused deck from your own study notes. It will save to your
          account and be available on any device where you sign in.
        </p>

        <form onSubmit={save} className="mt-8" data-id="element-3131" data-mp="2pnxqtqu9e3ob">
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-3132" data-mp="2unshpue1at9d">
            <label className="text-sm font-semibold text-[#334155] sm:col-span-2" data-id="element-3133" data-mp="2umhdjtgofjhq">
              Set name
              <input required maxLength={80} value={title} onChange={event => setTitle(event.target.value)} placeholder="e.g. Renal nutrition review" className={fieldClass} data-id="element-3134" data-mp="10asxfnyplavr" />
            </label>
            <label className="text-sm font-semibold text-[#334155] sm:col-span-2" data-id="element-3135" data-mp="j18ik3qveuxp">
              Description
              <textarea required rows={2} maxLength={180} value={description} onChange={event => setDescription(event.target.value)} placeholder="A quick reminder of what this deck covers" className={`${fieldClass} resize-y`} data-id="element-3136" data-mp="e2x7jpouaedw" />
            </label>
          </div>

          <div className="mt-8 border-t border-[#E2E8F0] pt-7" data-id="element-3137" data-mp="wt557s9rscmp">
            <div className="flex items-center justify-between gap-4" data-id="element-3138" data-mp="wuzgsfz5ise4">
              <div data-id="element-3139" data-mp="3mj0532m8mmoi">
                <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-3140" data-mp="3mjib2qypcf9m">Cards</h3>
                <p className="mt-1 text-sm text-[#64748B]" data-id="element-3141" data-mp="3hicf8k9qwbvv">
                  Add a prompt and its answer. Topics are optional.
                </p>
              </div>
              <span className="rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-bold text-[#2563EB]" data-id="element-3142" data-mp="g0opyi9jd5q8">
                {cards.length} {cards.length === 1 ? 'card' : 'cards'}
              </span>
            </div>

            <div className="mt-5 space-y-5" data-id="element-3143" data-mp="620niru1az7n">
              {cards.map((card, index) => <fieldset key={index} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5" data-id="element-3144" data-mp="trv0d9ecsnrw" data-mpi={index}>
                  <legend className="sr-only" data-id="element-3145" data-mp="ytkb0nxnyzfg">Card {index + 1}</legend>
                  <div className="flex items-center justify-between gap-4" data-id="element-3146" data-mp="kz9vmx7k4hm2">
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-id="element-3147" data-mp="p0v3u595c7px">
                      Card {index + 1}
                    </p>
                    {cards.length > 1 && <button type="button" onClick={() => setCards(current => current.filter((_, cardIndex) => cardIndex !== index))} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#B91C1C] transition-colors hover:text-[#991B1B]" data-id="element-3148" data-mp="1hi7j7fb504x9">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-3149" data-mp="2jaa350w1zoo1" /> Remove
                      </button>}
                  </div>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3150" data-mp="2t7oewn717pa0">
                    Topic{' '}
                    <span className="font-normal text-[#94A3B8]" data-id="element-3151" data-mp="2o823mjarkj3u">
                      (optional)
                    </span>
                    <input maxLength={60} value={card.topic} onChange={event => updateCard(index, 'topic', event.target.value)} placeholder="e.g. Electrolytes" className={fieldClass} data-id="element-3152" data-mp="8ge3yg4d8chc" />
                  </label>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3153" data-mp="1b7io267ss4id">
                    Prompt
                    <textarea required rows={3} value={card.front} onChange={event => updateCard(index, 'front', event.target.value)} placeholder="Write the question or cue" className={`${fieldClass} resize-y`} data-id="element-3154" data-mp="167e9wmud3lvb" />
                  </label>
                  <label className="mt-4 block text-sm font-semibold text-[#334155]" data-id="element-3155" data-mp="1pyiz8izxici7">
                    Answer
                    <textarea required rows={3} value={card.back} onChange={event => updateCard(index, 'back', event.target.value)} placeholder="Write the answer you want to remember" className={`${fieldClass} resize-y`} data-id="element-3156" data-mp="1ywuuiybqpygz" />
                  </label>
                </fieldset>)}
            </div>

            <button type="button" onClick={() => setCards(current => [...current, {
            topic: '',
            front: '',
            back: ''
          }])} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#93C5FD] bg-white px-4 py-2.5 text-sm font-semibold text-[#2563EB] transition-colors hover:border-[#2563EB] disabled:opacity-60" data-id="element-3157" data-mp="1zsezgchhe9jw">
              <PlusIcon className="h-4 w-4" data-id="element-3158" data-mp="3nhjll09hoxtl" /> Add another card
            </button>
          </div>

          <div aria-live="polite" className="mt-6" data-id="element-3159" data-mp="3iqtgzp8w0yzy">
            {error && <p role="alert" className="rounded-xl border border-[#FBD5D0] bg-[#FEF2F2] p-4 text-sm font-medium text-[#B91C1C]" data-id="element-3160" data-mp="3ioqguce0tphi">
                {error}
              </p>}
            {success && <p className="rounded-xl border border-[#C7EFE8] bg-[#F0FBF9] p-4 text-sm font-medium text-[#0F766E]" data-id="element-3161" data-mp="a9ynv1hw6sz2">
                {success}
              </p>}
          </div>

          <div className="mt-7 flex flex-wrap gap-3" data-id="element-3162" data-mp="c2fuik94e56">
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0" data-id="element-3163" data-mp="ewwdz059etb">
              {saving ? <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" data-id="element-3164" data-mp="wkq9chg50227" />
                  Saving set…
                </> : <>
                  Save to my account <ArrowRightIcon className="h-4 w-4" data-id="element-3165" data-mp="15isgzfudlsyg" />
                </>}
            </button>
            <button type="button" onClick={onCancel} disabled={saving} className="rounded-full border border-[#CBD5E1] px-6 py-3 text-sm font-semibold text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB] disabled:opacity-60" data-id="element-3166" data-mp="1acwp42fdo714">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>;
}
function Flashcards({
  set,
  onBack,
  onFinish
}: {
  set: FlashcardSet;
  onBack: () => void;
  onFinish: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const card = set.cards[index];
  const change = (direction: number) => {
    setIndex(value => (value + direction + set.cards.length) % set.cards.length);
    setShowBack(false);
  };
  return <section className="mx-auto max-w-3xl" data-id="element-3167" data-mp="39plkq4t7ws26">
      <div className="mb-5 flex items-center justify-between gap-4 text-xs font-bold uppercase tracking-[0.14em] text-[#94A3B8]" data-id="element-3168" data-mp="39oai48bidqcn">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 transition-colors hover:text-[#2563EB]" data-id="element-3169" data-mp="2h0ed8gbscs4s">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3170" data-mp="2c34nezkhiegf" /> All sets
        </button>
        <span data-id="element-3171" data-mp="2qqzl6udcn90d">
          {index + 1} / {set.cards.length}
        </span>
      </div>
      <p className="mb-4 text-sm font-semibold text-[#475569]" data-id="element-3172" data-mp="2lz7u8iqa719p">{set.title}</p>
      <button type="button" onClick={() => setShowBack(value => !value)} aria-pressed={showBack} aria-label={showBack ? 'Tap to review the prompt' : 'Tap to reveal the answer'} className="block w-full rounded-3xl [perspective:1600px]" data-id="element-3173" data-mp="1y9n85qytznw7">
        {/* Remount per card (key by index) so a new card appears front-facing
            instantly instead of animating a reverse flip on navigation. */}
        <motion.div key={index} className="relative h-[340px] w-full [transform-style:preserve-3d]" initial={false} animate={{
        rotateY: showBack ? 180 : 0
      }} transition={{
        duration: 0.5,
        ease: [0.22, 0.61, 0.36, 1]
      }} data-id="element-3174" data-mp="3uxwnd5jmtdao">
          {/* FRONT */}
          <FlashcardFace topic={card.topic} body={card.front} hint="Tap to reveal the answer" data-id="element-3175" data-mp="ihjavqggr492" />
          {/* BACK — pre-rotated so it faces the viewer once the card turns */}
          <FlashcardFace topic={card.topic} body={card.back} hint="Tap to review the prompt" back data-id="element-3176" data-mp="3378pq3h62q1r" />
        </motion.div>
      </button>
      <div className="mt-7 flex items-center justify-between" data-id="element-3177" data-mp="1dqd74do8ukco">
        <button type="button" onClick={() => change(-1)} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#475569] transition-colors hover:text-[#2563EB]" data-id="element-3178" data-mp="18s1vbxee1zrt">
          <ChevronLeftIcon className="h-4 w-4" data-id="element-3179" data-mp="z28u9qbvh31t" /> Previous
        </button>
        <button type="button" onClick={onFinish} className="text-xs font-bold uppercase tracking-[0.12em] text-[#94A3B8] transition-colors hover:text-[#0F172A]" data-id="element-3180" data-mp="v285pcv27bh1">
          Finish review
        </button>
        <button type="button" onClick={() => change(1)} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#475569] transition-colors hover:text-[#2563EB]" data-id="element-3181" data-mp="l53h76dqr3x2">
          Next <ChevronRightIcon className="h-4 w-4" data-id="element-3182" data-mp="2ahfaus8r27gz" />
        </button>
      </div>
    </section>;
}
function FlashcardFace({
  topic,
  body,
  hint,
  back = false
}: {
  topic: string;
  body: string;
  hint: string;
  back?: boolean;
}) {
  return <div className={`absolute inset-0 flex flex-col justify-between rounded-3xl border border-[#E2E8F0] p-7 text-left shadow-[0_4px_28px_rgba(15,23,42,.06)] [backface-visibility:hidden] md:p-10 ${back ? 'bg-[#F7F9FC] [transform:rotateY(180deg)]' : 'bg-white'}`} data-id="element-3183" data-mp="2myq7zjpmmtnu">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{
      color: TEAL
    }} data-id="element-3184" data-mp="201et4b55tizr">
        {topic}
      </span>
      <div className="max-w-2xl" data-id="element-3185" data-mp="2908nduv0pxa5">
        <p className="text-2xl font-bold leading-snug text-[#0F172A] md:text-3xl" data-id="element-3186" data-mp="290051ng4lzrk">
          {body}
        </p>
        <p className="mt-8 text-sm text-[#94A3B8]" data-id="element-3187" data-mp="2e1621xgtk96w">{hint}</p>
      </div>
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em]" style={{
      color: BLUE
    }} data-id="element-3188" data-mp="3kevn7jdzejcv">
        <RotateCcwIcon className="h-4 w-4" data-id="element-3189" data-mp="ederfpp3f9rl" /> Active recall
      </span>
    </div>;
}